import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemIcon,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import AddInfluencersDrawer from "../plans/AddInfluencersDrawer";
import { formatCompact, formatUsername } from "../plans/influencerFormat";
import {
  shareUrlFor,
  teamScope,
  useAddScriptCreators,
  useRemoveScript,
  useRotateBrandLink,
  useRotateCreatorLink,
  useScriptBoard,
} from "../../hooks/useScripts";
import ScriptReviewPanel from "./ScriptReviewPanel";
import {
  ApprovalPills,
  CreatorCell,
  LastActivity,
  STATUS_META,
  STATUS_ORDER,
  StatusChip,
  bodyCellSx,
  cardSx,
  copyText,
  errorMessage,
  headCellSx,
  pageSx,
  primaryButtonSx,
  secondaryButtonSx,
  ShareLinkMenu,
} from "./scriptUi";

// One plan's script board (/scripts/:planId): its creators (username +
// followers only), where each script stands, share links, and the script
// drawer. `?script=<id>` keeps the open drawer deep-linkable.

const ScriptRow = ({ script, onOpen, onRotate, onRemove }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const url = shareUrlFor(script.creatorToken);
  return (
    <TableRow hover onClick={() => onOpen(script.id)} sx={{ cursor: "pointer" }}>
      <TableCell sx={bodyCellSx}><CreatorCell script={script} subtitle={script.removedFromPlan} /></TableCell>
      <TableCell sx={bodyCellSx}>{formatCompact(script.followers)}</TableCell>
      <TableCell sx={bodyCellSx}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
          <StatusChip status={script.status} />
          <ApprovalPills approvals={script.approvals} />
        </Box>
      </TableCell>
      <TableCell sx={bodyCellSx}>{script.currentVersion ? `v${script.currentVersion}` : "—"}</TableCell>
      <TableCell sx={bodyCellSx}><LastActivity at={script.lastActivityAt} by={script.lastActivityBy} /></TableCell>
      <TableCell sx={bodyCellSx} align="right" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Copy creator link" arrow>
          <IconButton size="small" onClick={() => copyText(url, enqueueSnackbar, "Creator link")}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <ShareLinkMenu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          url={url}
          label="Creator link"
          onRotate={() => onRotate(script.id)}
          extraItems={
            <MenuItem onClick={() => { setAnchorEl(null); onRemove(script); }} sx={{ color: "error.main" }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>Remove from board
            </MenuItem>
          }
        />
      </TableCell>
    </TableRow>
  );
};

const ScriptBoard = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const openScriptId = searchParams.get("script");

  const { data, isLoading, error } = useScriptBoard(planId);
  const addCreators = useAddScriptCreators(planId);
  const removeScript = useRemoveScript(planId);
  const rotateCreatorLink = useRotateCreatorLink(planId);
  const rotateBrandLink = useRotateBrandLink(planId);

  const [addOpen, setAddOpen] = useState(false);
  const [statusTab, setStatusTab] = useState("all");
  const [search, setSearch] = useState("");
  const [brandMenuEl, setBrandMenuEl] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const scripts = useMemo(() => data?.scripts ?? [], [data]);
  const counts = useMemo(
    () => STATUS_ORDER.reduce((acc, s) => ({ ...acc, [s]: scripts.filter((x) => x.status === s).length }), {}),
    [scripts]
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^@/, "");
    return scripts.filter(
      (s) =>
        (statusTab === "all" || s.status === statusTab) &&
        (!q || (formatUsername(s.username) || "").toLowerCase().includes(q))
    );
  }, [scripts, statusTab, search]);

  const setOpenScript = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("script", id);
    else next.delete("script");
    setSearchParams(next, { replace: true });
  };

  const notify = (promise, success) =>
    promise
      .then(() => enqueueSnackbar(success, { variant: "success" }))
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));

  const handleRotateBrand = () =>
    notify(rotateBrandLink.mutateAsync(), "Brand link replaced — share the new one");
  const handleRotateCreator = (scriptId) =>
    notify(rotateCreatorLink.mutateAsync(scriptId), "Creator link replaced — share the new one");
  const handleConfirmRemove = () => {
    const script = pendingRemoval;
    setPendingRemoval(null);
    if (openScriptId === script.id) setOpenScript(null);
    notify(removeScript.mutateAsync(script.id), `@${formatUsername(script.username)} removed from the board`);
  };

  if (isLoading) {
    return (
      <>
        <DashboardHeaderSection hideSearch />
        <Box sx={{ ...pageSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress sx={{ color: "#FF5200" }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DashboardHeaderSection hideSearch />
        <Box sx={pageSx}>
          <Typography sx={{ color: "#B91C1C" }}>{errorMessage(error, "Couldn't load this script board.")}</Typography>
        </Box>
      </>
    );
  }

  const { plan, brandToken } = data;
  const brandUrl = shareUrlFor(brandToken);

  return (
    <>
      <DashboardHeaderSection hideSearch />
      <Box sx={pageSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <IconButton onClick={() => navigate("/scripts")} sx={{ bgcolor: "#fff", border: "1px solid #E5E7EB" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 700 }}>{plan.name}</Typography>
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Script approval{plan.brandName ? ` · ${plan.brandName}` : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={() => navigate(`/plans-summary/${plan.id}`)} sx={secondaryButtonSx}>
              Open plan
            </Button>
            <Tooltip title={brandUrl ? "One link for the brand to review every creator's script" : "Add a creator first"} arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  disabled={!brandUrl}
                  onClick={(e) => setBrandMenuEl(e.currentTarget)}
                  sx={secondaryButtonSx}
                >
                  Brand link
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={primaryButtonSx}>
              Add creators
            </Button>
          </Box>
        </Box>

        <Box sx={cardSx}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", px: 2, pt: 1.5 }}>
            <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)} variant="scrollable" sx={{ minHeight: 40 }}>
              <Tab value="all" label={`All (${scripts.length})`} sx={{ textTransform: "none", minHeight: 40 }} />
              {STATUS_ORDER.map((s) => (
                <Tab key={s} value={s} label={`${STATUS_META[s].label} (${counts[s]})`} sx={{ textTransform: "none", minHeight: 40 }} />
              ))}
            </Tabs>
            <TextField
              size="small"
              placeholder="Search username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 240 }, "& .MuiInputBase-root": { borderRadius: "10px" } }}
            />
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, minWidth: 220 }}>Username</TableCell>
                  <TableCell sx={headCellSx}>Followers</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Version</TableCell>
                  <TableCell sx={headCellSx}>Last activity</TableCell>
                  <TableCell sx={headCellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8, border: 0 }}>
                      <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {scripts.length === 0 ? "No creators on this board yet" : "No creators match"}
                      </Typography>
                      {scripts.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Add creators from this plan's lists to start sharing scripts.
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((script) => (
                    <ScriptRow
                      key={script.id}
                      script={script}
                      onOpen={setOpenScript}
                      onRotate={handleRotateCreator}
                      onRemove={setPendingRemoval}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>

      <ShareLinkMenu
        anchorEl={brandMenuEl}
        onClose={() => setBrandMenuEl(null)}
        url={brandUrl}
        label="Brand link"
        onRotate={handleRotateBrand}
      />

      <AddInfluencersDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        lists={plan.lists}
        planId={plan.id}
        mutation={addCreators}
        title="Add creators"
      />

      <Drawer
        anchor="right"
        open={Boolean(openScriptId)}
        onClose={() => setOpenScript(null)}
        PaperProps={{ sx: { width: isMobile ? "100%" : { sm: 560, md: 680 }, maxWidth: "100vw" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, borderBottom: "1px solid #F3F4F6" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Script</Typography>
          <IconButton size="small" onClick={() => setOpenScript(null)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto" }}>
          {openScriptId && <ScriptReviewPanel scope={teamScope(planId)} scriptId={openScriptId} viewer="team" />}
        </Box>
      </Drawer>

      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove from the board?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            @{formatUsername(pendingRemoval?.username)}'s script, its history and their share link will stop being
            available. The creator stays in the plan.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPendingRemoval(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove} sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScriptBoard;
